import React, {useEffect, useMemo, useState} from "react";
import Header from "../components/header/Header";
import Greeting from "./greeting/Greeting";
import Skills from "./skills/Skills";
import ProfessionalJourney from "./professionalJourney/ProfessionalJourney";
import StackProgress from "./skillProgress/skillProgress";
import WorkExperience from "./workExperience/WorkExperience";
import Portfolio from "./portfolio/Portfolio";
import Achievement from "./achievement/Achievement";
import Blogs from "./blogs/Blogs";
import Footer from "../components/footer/Footer";
import Talks from "./talks/Talks";
import Podcast from "./podcast/Podcast";
import Education from "./education/Education";
import ScrollToTopButton from "./topbutton/Top";
import Twitter from "./twitter-embed/twitter";
import Profile from "./profile/Profile";
import SplashScreen from "./splashScreen/SplashScreen";
import {splashScreen, theme} from "../portfolio";
import {StyleProvider} from "../contexts/StyleContext";
import "./Main.scss";

function clamp(n, min, max, fallback) {
  const num = typeof n === "number" && Number.isFinite(n) ? n : fallback;
  return Math.min(max, Math.max(min, num));
}

function asString(v) {
  return typeof v === "string" ? v.trim() : "";
}

function getSectionBlendConfig() {
  const blend = theme?.sections?.blend && typeof theme.sections.blend === "object"
    ? theme.sections.blend
    : {};

  return {
    enabled: blend.enabled !== false,
    startPct: clamp(blend.startPct, 0, 100, 70),
    endPct: clamp(blend.endPct, 0, 100, 100),
  };
}

function getSectionBg(sectionKey) {
  // 1) Per-section override (Theme > Section Backgrounds)
  const s = theme?.sections?.[sectionKey];

  if (s && typeof s === "object") {
    const override = Boolean(s.override);
    const bg = asString(s.bg);
    if (override && bg) return bg;
  } else if (typeof s === "string") {
    // Back-compat: allow shorthand { greeting: "#..." }
    const bg = asString(s);
    if (bg) return bg;
  }

  // 2) Global background
  const globalBg = asString(theme?.bg);
  return globalBg || "#0a0a0a";
}


function buildSectionTokenVars(sectionKey) {
  const st = (theme && theme.sectionTokens && typeof theme.sectionTokens === "object")
    ? theme.sectionTokens
    : {};
  const t = st[sectionKey];
  if (!t || typeof t !== "object") return {};

  const vars = {};

  // Greeting
  if (sectionKey === "greeting") {
    if (asString(t.title)) vars["--sh-greeting-title"] = asString(t.title);
    if (asString(t.subtitle)) vars["--sh-greeting-subtitle"] = asString(t.subtitle);

    const b = t.buttons && typeof t.buttons === "object" ? t.buttons : {};
    if (asString(b.bg)) vars["--sh-buttons-primary-bg"] = asString(b.bg);
    if (asString(b.text)) vars["--sh-buttons-primary-text"] = asString(b.text);
    if (asString(b.border)) vars["--sh-buttons-primary-border"] = asString(b.border);

    if (asString(b.hoverBg)) vars["--sh-buttons-primary-hover-bg"] = asString(b.hoverBg);
    if (asString(b.hoverText)) vars["--sh-buttons-primary-hover-text"] = asString(b.hoverText);
    if (asString(b.hoverBorder)) vars["--sh-buttons-primary-hover-border"] = asString(b.hoverBorder);

    if (asString(b.focus)) vars["--sh-buttons-focus"] = asString(b.focus);
  }

  // Professional Journey
  if (sectionKey === "professionalJourney") {
    if (asString(t.title)) vars["--sh-professional-journey-title"] = asString(t.title);
    if (asString(t.subtitle)) vars["--sh-professional-journey-subtitle"] = asString(t.subtitle);
    if (asString(t.description)) vars["--sh-professional-journey-description"] = asString(t.description);
    if (asString(t.kicker)) vars["--sh-professional-journey-kicker"] = asString(t.kicker);
    if (asString(t.cardBg)) vars["--sh-professional-journey-card-bg"] = asString(t.cardBg);
    if (asString(t.border)) vars["--sh-professional-journey-border"] = asString(t.border);
  }

  // Skills
  if (sectionKey === "skills") {
    if (asString(t.title)) vars["--sh-skills-title"] = asString(t.title);
    if (asString(t.desc)) vars["--sh-skills-desc"] = asString(t.desc);
    if (asString(t.skillText)) vars["--sh-skills-skill"] = asString(t.skillText);
    if (asString(t.icon)) vars["--sh-skills-icon"] = asString(t.icon);
    if (asString(t.iconHover)) vars["--sh-skills-icon-hover"] = asString(t.iconHover);

  }

  // Education
  if (sectionKey === "education") {
    if (asString(t.title)) vars["--sh-education-title"] = asString(t.title);
    if (asString(t.desc)) vars["--sh-education-desc"] = asString(t.desc);
    if (asString(t.hover)) vars["--sh-education-card-border"] = asString(t.hover);
  }

  // Experience
  if (sectionKey === "experience") {
    if (asString(t.title)) vars["--sh-experience-title"] = asString(t.title);
    if (asString(t.role)) vars["--sh-experience-role"] = asString(t.role);
    if (asString(t.desc)) vars["--sh-experience-desc"] = asString(t.desc);
    if (asString(t.company)) vars["--sh-experience-company"] = asString(t.company);
    if (asString(t.date)) vars["--sh-experience-date"] = asString(t.date);
    if (asString(t.bullets)) vars["--sh-experience-bullets"] = asString(t.bullets);
    if (asString(t.detailsBg)) vars["--sh-experience-details-bg"] = asString(t.detailsBg);
    if (asString(t.outline)) vars["--sh-experience-outline"] = asString(t.outline);
  }

  // Portfolio
  if (sectionKey === "portfolio") {
    if (asString(t.title)) vars["--sh-portfolio-title"] = asString(t.title);
    if (asString(t.subtitle)) vars["--sh-portfolio-subtitle"] = asString(t.subtitle);
    if (asString(t.cardBg)) vars["--sh-portfolio-card-bg"] = asString(t.cardBg);
    if (asString(t.cardHoverBg)) vars["--sh-portfolio-card-hover-bg"] = asString(t.cardHoverBg);
    if (asString(t.cardGlow)) vars["--sh-portfolio-card-glow"] = asString(t.cardGlow);
  }

  // Contact
  if (sectionKey === "contact") {
    if (asString(t.imageRing)) vars["--sh-github-border"] = asString(t.imageRing);
  }

  return vars;
}

function buildSectionStyle(sectionKey, nextKey) {
  const {enabled, startPct, endPct} = getSectionBlendConfig();
  const a = getSectionBg(sectionKey);
  const b = nextKey ? getSectionBg(nextKey) : a;

  const vars = buildSectionTokenVars(sectionKey);

  // Skills: optional media opacity (0..1)
  if (sectionKey === "skills") {
    const st = (theme && theme.sectionTokens && typeof theme.sectionTokens === "object")
      ? theme.sectionTokens
      : {};
    const t = st.skills && typeof st.skills === "object" ? st.skills : {};
    const m = t.media && typeof t.media === "object" ? t.media : {};
    const op = m.opacity;
    if (typeof op === "number" && Number.isFinite(op)) {
      vars["--sh-skills-video-opacity"] = String(clamp(op, 0, 1, 0.45));
    }
  }

  if (enabled && nextKey && a && b && a !== b) {
    return {
      background: `linear-gradient(180deg, ${a} 0%, ${a} ${startPct}%, ${b} ${endPct}%)`,
      ...vars,
    };
  }

  return {background: a, ...vars};
}

const Main = () => {
  // Site is permanently dark themed
  const isDark = true;

  const [isShowingSplashAnimation, setIsShowingSplashAnimation] =
    useState(true);

  useEffect(() => {
    if (splashScreen.enabled) {
      const splashTimer = setTimeout(
        () => setIsShowingSplashAnimation(false),
        splashScreen.duration
      );
      return () => {
        clearTimeout(splashTimer);
      };
    }
  }, []);

  const changeTheme = () => {};

  const sectionList = useMemo(
    () => [
      {key: "greeting", node: <Greeting />},
      {key: "professionalJourney", node: <ProfessionalJourney />},
      {key: "skills", node: <Skills />},
      {key: "skillProgress", node: <StackProgress />},
      {key: "education", node: <Education />},
      {key: "experience", node: <WorkExperience />},
      {key: "portfolio", node: <Portfolio />},
      {key: "achievement", node: <Achievement />},
      {key: "blogs", node: <Blogs />},
      {key: "talks", node: <Talks />},
      {key: "twitter", node: <Twitter />},
      {key: "podcast", node: <Podcast />},
      {key: "contact", node: <Profile />},
      {key: "footer", node: <Footer />},
    ],
    []
  );

  return (
    <div className={isDark ? "dark-mode" : null}>
      <StyleProvider value={{isDark: isDark, changeTheme: changeTheme}}>
        {isShowingSplashAnimation && splashScreen.enabled ? (
          <SplashScreen />
        ) : (
          <>
            <Header />

            {sectionList.map((s, i) => (
              <div
                key={s.key}
                className="sh-section"
                data-section={s.key}
                style={buildSectionStyle(s.key, sectionList[i + 1]?.key)}
              >
                {s.node}
              </div>
            ))}

            <ScrollToTopButton />
          </>
        )}
      </StyleProvider>
    </div>
  );
};

export default Main;
